import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Select,
  InputNumber,
  Input,
  Button,
  Space,
  message,
  Typography,
  List,
  Card,
  Tag
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import dayjs, { Dayjs } from 'dayjs';
import api from '../../services/api';
import { DatePickerModal } from '../common/DatePickerModal';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface StockInModalMobileProps {
  visible: boolean;
  onCancel: () => void;
  editingRecord?: any;
  medicines: any[];
  suppliers: any[];
  isLoadingDetail?: boolean;
}

interface StockInItem {
  medicine_id: number;
  quantity: number;
  unit_price: number;
  lot_number: string;
  expiry_date: any;
  manufacturing_date: any;
  notes?: string;
}

const StockInModalMobile: React.FC<StockInModalMobileProps> = ({
  visible,
  onCancel,
  editingRecord,
  medicines,
  suppliers,
  isLoadingDetail = false
}) => {
  const [form] = Form.useForm();
  const [itemForm] = Form.useForm();
  const [items, setItems] = useState<StockInItem[]>([]);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [datePickerState, setDatePickerState] = useState<{
    isOpen: boolean;
    type: 'expiry' | 'manufacturing' | null;
    currentDate: Dayjs | null;
  }>({
    isOpen: false,
    type: null,
    currentDate: null
  });
  const queryClient = useQueryClient();

  // Mutations
  const createMutation = useMutation(
    (data: any) => api.post('/api/stock/in', data),
    {
      onSuccess: () => {
        message.success('Tạo phiếu nhập kho thành công!');
        queryClient.invalidateQueries('stock-in');
        handleCancelInternal();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi tạo phiếu nhập kho');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => api.put(`/api/stock/in/${id}`, data),
    {
      onSuccess: () => {
        message.success('Cập nhật phiếu nhập kho thành công!');
        queryClient.invalidateQueries('stock-in');
        handleCancelInternal();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật phiếu nhập kho');
      },
    }
  );

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (visible) {
      if (editingRecord && !isLoadingDetail) {
        // Edit mode - populate form with existing data
        let voucherData, itemsData;
        if (editingRecord.voucher && editingRecord.items) {
          voucherData = editingRecord.voucher;
          itemsData = editingRecord.items;
        } else {
          setInitialValues({ items: [] });
          setItems([]);
          setIsFormInitialized(true);
          return;
        }
        const processedItems = itemsData.map((item: any) => ({
          medicine_id: item.medicine_id,
          quantity: item.quantity || 0,
          unit_price: item.unit_price || 0,
          lot_number: item.lot_number || '',
          expiry_date: item.expiry_date ? dayjs(item.expiry_date) : null,
          manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date) : null,
          notes: item.notes || ''
        }));
        const formData = {
          supplier_id: voucherData.supplier_id,
          notes: voucherData.notes || ''
        };
        setInitialValues(formData);
        setItems(processedItems);
        setIsFormInitialized(true);
      } else if (!editingRecord && !isLoadingDetail) {
        setInitialValues({ items: [] });
        setItems([]);
        setIsFormInitialized(true);
      }
    } else {
      setInitialValues(null);
      setItems([]);
      setIsFormInitialized(false);
    }
  }, [visible, editingRecord, isLoadingDetail]);

  // Đảm bảo form được set values sau khi initialValues được set
  useEffect(() => {
    if (initialValues && isFormInitialized && visible) {
      setTimeout(() => {
        form.setFieldsValue(initialValues);
      }, 200);
    }
  }, [initialValues, isFormInitialized, visible, form]);

  const handleCancelInternal = () => {
    form.resetFields();
    itemForm.resetFields();
    setInitialValues(null);
    setItems([]);
    setIsFormInitialized(false);
    setDatePickerState({
      isOpen: false,
      type: null,
      currentDate: null
    });
    onCancel();
  };

  const handleAddItem = () => {
    itemForm.validateFields().then((values) => {
      const newItem: StockInItem = {
        medicine_id: values.medicine_id,
        quantity: values.quantity,
        unit_price: values.unit_price,
        lot_number: values.lot_number,
        expiry_date: values.expiry_date,
        manufacturing_date: values.manufacturing_date,
        notes: values.notes || ''
      };
      setItems([...items, newItem]);
      itemForm.resetFields();
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const openDatePicker = (type: 'expiry' | 'manufacturing', currentDate?: Dayjs) => {
    setDatePickerState({
      isOpen: true,
      type,
      currentDate: currentDate || null
    });
  };

  const closeDatePicker = () => {
    setDatePickerState({
      isOpen: false,
      type: null,
      currentDate: null
    });
  };

  const handleDateSelect = (date: Dayjs) => {
    if (datePickerState.type === 'expiry') {
      itemForm.setFieldValue('expiry_date', date);
    } else if (datePickerState.type === 'manufacturing') {
      itemForm.setFieldValue('manufacturing_date', date);
    }
    closeDatePicker();
  };

  const handleSubmit = async (values: any) => {
    try {
      if (items.length === 0) {
        message.error('Vui lòng thêm ít nhất một loại thuốc');
        return;
      }
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.manufacturing_date && item.expiry_date) {
          if (item.manufacturing_date.isAfter(item.expiry_date, 'day')) {
            message.error(`Thuốc ${i + 1}: Ngày sản xuất không được sau hạn sử dụng`);
            return;
          }
        }
      }
      const data = {
        supplier_id: values.supplier_id,
        notes: values.notes || '',
        items: items.map((item: StockInItem) => ({
          medicine_id: item.medicine_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          lot_number: item.lot_number,
          expiry_date: item.expiry_date.format('YYYY-MM-DD'),
          manufacturing_date: item.manufacturing_date.format('YYYY-MM-DD'),
          notes: item.notes || ''
        }))
      };
      if (editingRecord) {
        const id = editingRecord.voucher?.id || editingRecord.id;
        await updateMutation.mutateAsync({ id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {}
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  return (
    <Drawer
      title={editingRecord ? 'Sửa phiếu nhập kho' : 'Tạo phiếu nhập kho'}
      open={visible}
      onClose={handleCancelInternal}
      placement="bottom"
      height="90vh"
      bodyStyle={{ padding: 12, background: '#f5f6fa' }}
      destroyOnClose
      className="stockin-mobile-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValues}
        style={{ width: '100%' }}
      >
        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Form.Item
            name="supplier_id"
            label="Nhà cung cấp"
            rules={[{ required: true, message: 'Vui lòng chọn nhà cung cấp' }]}
          >
            <Select placeholder="Chọn nhà cung cấp">
              {suppliers.map((supplier: any) => (
                <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Ghi chú">
            <TextArea rows={2} placeholder="Nhập ghi chú (nếu có)" />
          </Form.Item>
        </Card>
        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Thêm thuốc</Title>
          <Form form={itemForm} layout="vertical">
            <Form.Item
              name="medicine_id"
              label="Thuốc"
              rules={[{ required: true, message: 'Chọn thuốc' }]}
            >
              <Select placeholder="Chọn thuốc">
                {medicines.map(medicine => (
                  <Option key={medicine.id} value={medicine.id}>{medicine.name} ({medicine.code})</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="quantity" label="Số lượng" rules={[{ required: true, message: 'Nhập số lượng' }]}> 
              <InputNumber min={1} style={{ width: '100%' }} placeholder="Số lượng" />
            </Form.Item>
            <Form.Item name="unit_price" label="Đơn giá" rules={[{ required: true, message: 'Nhập đơn giá' }]}> 
              <InputNumber min={0} style={{ width: '100%' }} placeholder="Đơn giá" />
            </Form.Item>
            <Form.Item name="lot_number" label="Số lô" rules={[{ required: true, message: 'Nhập số lô' }]}> 
              <Input style={{ width: '100%' }} placeholder="Số lô" />
            </Form.Item>
            <Form.Item name="expiry_date" label="Hạn sử dụng" rules={[{ required: true, message: 'Chọn hạn sử dụng' }]}> 
              <div style={{ display: 'flex', gap: 8 }}>
                <Input 
                  placeholder="Hạn sử dụng" 
                  value={itemForm.getFieldValue('expiry_date') ? dayjs(itemForm.getFieldValue('expiry_date')).format('DD/MM/YYYY') : ''}
                  readOnly
                  style={{ flex: 1 }}
                />
                <Button 
                  type="primary" 
                  icon={<CalendarOutlined />} 
                  onClick={() => openDatePicker('expiry', itemForm.getFieldValue('expiry_date'))}
                />
              </div>
            </Form.Item>
            <Form.Item name="manufacturing_date" label="Ngày sản xuất" rules={[{ required: true, message: 'Chọn ngày sản xuất' }]}> 
              <div style={{ display: 'flex', gap: 8 }}>
                <Input 
                  placeholder="Ngày sản xuất" 
                  value={itemForm.getFieldValue('manufacturing_date') ? dayjs(itemForm.getFieldValue('manufacturing_date')).format('DD/MM/YYYY') : ''}
                  readOnly
                  style={{ flex: 1 }}
                />
                <Button 
                  type="primary" 
                  icon={<CalendarOutlined />} 
                  onClick={() => openDatePicker('manufacturing', itemForm.getFieldValue('manufacturing_date'))}
                />
              </div>
            </Form.Item>
            <Form.Item name="notes" label="Ghi chú">
              <Input style={{ width: '100%' }} placeholder="Ghi chú (nếu có)" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem} block>Thêm thuốc</Button>
            </Form.Item>
          </Form>
        </Card>
        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Danh sách thuốc</Title>
          <List
            dataSource={items}
            renderItem={(item, idx) => {
              const medicine = medicines.find(m => m.id === item.medicine_id);
              return (
                <List.Item
                  actions={[
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveItem(idx)} size="small" />
                  ]}
                >
                  <Card style={{ width: '100%', borderRadius: 8, marginBottom: 0 }}>
                    <div style={{ fontWeight: 500 }}>{medicine?.name || 'N/A'} <Tag>{medicine?.code}</Tag></div>
                    <div style={{ fontSize: 13 }}>Số lượng: <b>{item.quantity}</b> | Đơn giá: <b>{item.unit_price.toLocaleString('vi-VN')}</b> | Số lô: <b>{item.lot_number}</b></div>
                    <div style={{ fontSize: 12, color: '#888' }}>HSD: {item.expiry_date ? dayjs(item.expiry_date).format('DD/MM/YYYY') : 'N/A'} | NSX: {item.manufacturing_date ? dayjs(item.manufacturing_date).format('DD/MM/YYYY') : 'N/A'}</div>
                    {item.notes && <div style={{ fontSize: 12, color: '#888' }}>Ghi chú: {item.notes}</div>}
                  </Card>
                </List.Item>
              );
            }}
            locale={{ emptyText: 'Chưa có thuốc nào' }}
          />
        </Card>
        <Card style={{ borderRadius: 10, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>Tổng tiền:</Text>
            <Text strong style={{ color: '#52c41a', fontSize: 18 }}>{totalAmount.toLocaleString('vi-VN')} VNĐ</Text>
          </div>
        </Card>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancelInternal}>Hủy</Button>
          <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>{editingRecord ? 'Cập nhật' : 'Lưu phiếu'}</Button>
        </Space>
      </Form>

      {/* DatePickerModal */}
      <DatePickerModal
        isOpen={datePickerState.isOpen}
        onClose={closeDatePicker}
        onSelect={handleDateSelect}
        selectedDate={datePickerState.currentDate || undefined}
        title={datePickerState.type === 'expiry' ? 'Chọn hạn sử dụng' : 'Chọn ngày sản xuất'}
      />
    </Drawer>
  );
};

export default StockInModalMobile;
